import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import styles from "./RangeInput.module.css";

/**
 * RangeInput
 *  type ['text', 'email', 'password', 'number', 'color', 'search', 'tel'] 를 지원함
 * @type React.FC<RangeInputPropTypes>
 */
const RangeInput = React.forwardRef((props, ref) => {
  Object.keys(props).forEach((p) => {
    if (!Object.keys(RangeInputPropTypes).includes(p)) {
      console.error(`RangeInput: 정의되지 않은 prop ${p}가 전달되었습니다.`);
    }
  });

  /** 외부에서 접근할 수 있는 함수들 */
  React.useImperativeHandle(ref, () => ({
    /** 값 삭제(초기화) */
    clear() {
      setCurrState((prev) => ({
        ...prev,
        startCurrValue: props.startDefaultValue ?? "",
        endCurrValue: props.endDefaultValue ?? "",
      }));
    },
    startClear() {
      setCurrState((prev) => ({
        ...prev,
        startCurrValue: props.startDefaultValue ?? "",
      }));
    },
    endClear() {
      setCurrState((prev) => ({
        ...prev,
        endCurrValue: props.endDefaultValue ?? "",
      }));
    },
    /** 값 삭제(초기화) */
    /** 값 가져오기 */
    getValue() {
      return { startValue: currState.startCurrValue, endValue: currState.endCurrValue };
    },
    /** 값 지정 */
    setStartValue(v) {
      setCurrState((prev) => ({
        ...prev,
        startCurrValue: v !== null ? v : "",
      }));
    },
    setEndValue(v) {
      setCurrState((prev) => ({
        ...prev,
        endCurrValue: v !== null ? v : "",
      }));
    },
    /** 현재 input에 focus 처리 */
    focus() {
      if (!refInput.current) {
        console.warn("RangeInput: refInput 가 아직 준비되지 않았습니다.");
      }
      refInput.current?.focus();
    },
    /* enable */
    enable() {
      setCurrState((prev) => ({
        ...prev,
        startDisabled: false,
        endDisabled: false,
      }));
    },
    startEnable() {
      setCurrState((prev) => ({
        ...prev,
        startDisabled: false,
      }));
    },
    endEnable() {
      setCurrState((prev) => ({
        ...prev,
        endDisabled: false,
      }));
    },
    /* enable */
    /* disable */
    disable() {
      setCurrState((prev) => ({
        ...prev,
        startDisabled: true,
        endDisabled: true,
      }));
    },
    startDisable() {
      setCurrState((prev) => ({
        ...prev,
        startDisabled: true,
      }));
    },
    endDisable() {
      setCurrState((prev) => ({
        ...prev,
        endDisabled: true,
      }));
    },
    /* disable */
    /* readonly */
    setReadOnly: (isReadOnly) => {
      setCurrState((prev) => ({
        ...prev,
        startReadOnly: isReadOnly,
        endReadOnly: isReadOnly,
      }));
    },
    setStartReadOnly: (isReadOnly) => {
      setCurrState((prev) => ({
        ...prev,
        startReadOnly: isReadOnly,
      }));
    },
    setEndReadOnly: (isReadOnly) => {
      setCurrState((prev) => ({
        ...prev,
        endReadOnly: isReadOnly,
      }));
    },
    /* readonly */
    updateState(stateName, newValue) {
      // currState에 있는 값만 업데이트 가능
      if (Object.keys(currState).includes(stateName)) {
        setCurrState((prev) => ({
          ...prev,
          [stateName]: newValue,
        }));
      } else {
        console.error("RangeInput: State에 지정되지 않은 값을 업데이트 시도하였습니다. 잘못된 시도입니다.");
      }
    },
  }));

  useEffect(() => {
    const keyPair = props.divClassName;
    if (keyPair === "keyValuePairs") {
      setCurrState((prev) => ({
        ...prev,
        startDisabled: true,
      }));
    }
  }, []);

  const refStartInput = useRef();
  const refEndInput = useRef();

  /**
   * RangeInput Component의 State
   */
  const [currState, setCurrState] = useState({
    startDisabled: props.startDisabled,
    endDisabled: props.endDisabled,
    startReadOnly: props.startReadOnly,
    endReadOnly: props.endReadOnly,
    startCurrValue: props.startDefaultValue,
    endCurrValue: props.endDefaultValue,
    startPlaceholder: props.startPlaceholder,
    endPlaceholder: props.endPlaceholder,
    startMinLength: props.startMinLength,
    endMinLength: props.endMinLength,
    startMaxLength: props.startMaxLength,
    endMaxLength: props.endMaxLength,
    startMin: props.startMin,
    endMin: props.endMin,
    startMax: props.startMax,
    endMax: props.endMax,
  });

  /**
   * 변경 이벤트
   */
  const _startOnChange = (e) => {
    const { value } = e.target;

    setCurrState((prev) => ({
      ...prev,
      startCurrValue: value,
    }));

    if (props.startOnChange) {
      props.startOnChange(e);
    }
  };

  const _endOnChange = (e) => {
    const { value } = e.target;

    setCurrState((prev) => ({
      ...prev,
      endCurrValue: value,
    }));

    if (props.endOnChange) {
      props.endOnChange(e);
    }
  };

  /**
   * 키 up 입력 이벤트
   */
  const _startOnKeyUp = (e) => {
    if (e.key === "Enter") {
      if (props.startOnEnterPress) {
        props.startOnEnterPress(e);
      }
    } else {
      if (props.startOnKeyUp) {
        props.startOnKeyUp(e);
      }
    }
  };
  const _endOnKeyUp = (e) => {
    if (e.key === "Enter") {
      if (props.endOnEnterPress) {
        props.endOnEnterPress(e);
      }
    } else {
      if (props.endOnKeyUp) {
        props.endOnKeyUp(e);
      }
    }
  };

  /**
   * 키 down 입력 이벤트
   */
  const _startOnKeyDown = (e) => {
    if (props.startOnKeyDown) {
      props.startOnKeyDown(e);
    }
  };
  const _endOnKeyDown = (e) => {
    if (props.endOnKeyDown) {
      props.endOnKeyDown(e);
    }
  };

  return (
    <div className={`${styles.container} ${props.divClassName ?? ""}`}>
      <input
        className={`${styles.e3Input} ${styles.startInput} ${props.controlWidth ?? ""} ${
          props.startInputControlWidth ?? ""
        } ${props.className ?? ""}`}
        type={props.type}
        ref={refStartInput}
        style={props.startInputStyle}
        disabled={currState.startDisabled}
        readOnly={currState.startReadOnly}
        value={currState.startCurrValue}
        placeholder={currState.startPlaceholder}
        {...(currState.startMinLength && { minLength: currState.startMinLength })}
        {...(currState.startMaxLength && { maxLength: currState.startMaxLength })}
        {...(currState.startMin && { min: currState.startMin })}
        {...(currState.startMax && { max: currState.startMax })}
        onChange={_startOnChange}
        onKeyDown={_startOnKeyDown}
        onKeyUp={_startOnKeyUp}
      />
      {props.gubun}
      <input
        className={`${styles.e3Input} ${styles.endInput} ${props.controlWidth ?? ""} ${
          props.endInputControlWidth ?? ""
        } ${props.className ?? ""}`}
        type={props.type}
        ref={refEndInput}
        style={props.endInputStyle}
        disabled={currState.endDisabled}
        readOnly={currState.endReadOnly}
        value={currState.endCurrValue}
        placeholder={currState.endPlaceholder}
        {...(currState.endMinLength && { minLength: currState.endMinLength })}
        {...(currState.endMaxLength && { maxLength: currState.endMaxLength })}
        {...(currState.endMin && { min: currState.endMin })}
        {...(currState.endMax && { max: currState.endMax })}
        onChange={_endOnChange}
        onKeyDown={_endOnKeyDown}
        onKeyUp={_endOnKeyUp}
      />
    </div>
  );
});

const RangeInputPropTypes = {
  /**
   * type: input type https://developer.mozilla.org/ko/docs/Web/HTML/Element/input#input_%EC%9C%A0%ED%98%95
   *  - text: 일반 텍스트
   *  - email: email 유형 텍스트 (키보드 이메일에 맞게 전환)
   *  - password: password 유형 텍스트 (자동 텍스트 숨김)
   *  - number: 숫자 스피너 표기. min, max 지정 가능
   *  - color: 컬러 피커
   *  - search: 캐시에 저장 기능 있음
   *  - tel: 전화번호
   *  - url: url 텍스트 (텍스트입력처럼 보이지만 검증 매개변수가 있음)
   *
   */
  type: PropTypes.oneOf(["text", "email", "password", "number", "color", "search", "tel"]),
  /**
   * disabled: 비활성화
   * 기본값 false
   * updateState('disabled', bool) 함수로 수정 가능
   */
  startDisabled: PropTypes.bool,
  endDisabled: PropTypes.bool,
  /**
   * readOnly: 읽기전용
   * 기본값 false
   * updateState('readOnly', bool) 함수로 수정 가능
   */
  startReadOnly: PropTypes.bool,
  endReadOnly: PropTypes.bool,
  /**
   * defaultValue: 최초 바인딩 되는 값
   * setValue()를 이용해 value 값 수정 가능
   */
  startDefaultValue: PropTypes.string,
  endDefaultValue: PropTypes.string,
  /**
   * 값이 비어있을 때 나타나는 내용
   * 기본값 없음
   * updateState('placeholder', string) 함수로 수정 가능
   */
  startPlaceholder: PropTypes.string,
  endPlaceholder: PropTypes.string,
  /**
   * minLength: 텍스트 최소길이
   * 기본값 없음
   * updateState('minLength', number) 함수로 수정 가능
   */
  startMinLength: PropTypes.number,
  endMinLength: PropTypes.number,
  /**
   * maxLength: 텍스트 최대길이
   * 기본값 없음
   * updateState('maxLength', number) 함수로 수정 가능
   */
  startMaxLength: PropTypes.number,
  endMaxLength: PropTypes.number,
  /**
   * min: type이 'number' 인 경우에만 유효. 최소값
   * 기본값 없음
   * updateState('min', number) 함수로 수정 가능
   */
  startMin: PropTypes.number,
  endMin: PropTypes.number,
  /**
   * max: type이 'number' 인 경우에만 유효. 최대값
   * 기본값 없음
   * updateState('max', number) 함수로 수정 가능
   */
  startMax: PropTypes.number,
  endMax: PropTypes.number,
  /**
   * 변경 이벤트
   */
  startOnChange: PropTypes.func,
  endOnChange: PropTypes.func,
  /**
   * 엔터키 입력 이벤트
   */
  startOnEnterPress: PropTypes.func,
  endOnEnterPress: PropTypes.func,
  /**
   * 키 입력 이벤트
   */
  startOnKeyUp: PropTypes.func,
  endOnKeyUp: PropTypes.func,
  /**
   *
   */
  startOnKeyDown: PropTypes.func,
  endOnKeyDown: PropTypes.func,
  /** */
  controlWidth: PropTypes.string,
  startInputControlWidth: PropTypes.string,
  endInputControlWidth: PropTypes.string,
  /**
   * className
   */
  className: PropTypes.string,
  /**
   * container Div className
   */
  divClassName: PropTypes.string,
  startInputStyle: PropTypes.object,
  endInputStyle: PropTypes.object,
  gubun: PropTypes.string,
};

RangeInput.propTypes = RangeInputPropTypes;
RangeInput.defaultProps = {
  type: "text",
  startDisabled: false,
  endDisabled: false,
  startReadOnly: false,
  endReadOnly: false,
  startDefaultValue: "",
  endDefaultValue: "",
  startPlaceholder: "",
  endPlaceholder: "",
  startMinLength: null,
  endMinLength: null,
  startMaxLength: null,
  endMaxLength: null,
  startMin: null,
  endMin: null,
  startMax: null,
  endMax: null,
  startOnChange: null,
  endOnChange: null,
  startOnEnterPress: null,
  endOnEnterPress: null,
  startOnKeyUp: null,
  endOnKeyUp: null,
  startOnKeyDown: null,
  endOnKeyDown: null,
  controlWidth: null,
  startInputControlWidth: null,
  endInputControlWidth: null,
  className: null,
  divClassName: "keyValuePairs",
  startInputStyle: null,
  endInputStyle: null,
  gubun: null,
};
RangeInput.displayName = "RangeInput";
export default RangeInput;
